import throttle from 'lodash/throttle';

import type { CoachPage } from '~/utils/coach/coachRoutes';

import { COACH_ROUTE_INDEX } from '~/constants/routes/coach';
import { coachRoutePath } from '~/utils/coach/coachRoutes';

/**
 * How everything inside the coach shell moves between its surfaces. The four functions are the
 * same four they always were, but what they move is the URL's path: each ambient surface is a
 * child route of `/coach`, so a page the student is looking at is a page they can link to, reload
 * and walk back out of with the browser's own button.
 *
 * Callers still name pages the way the catalogue does (`wallet`, `learn-library`) — those names are
 * on the wire, in recommendations the coach writes — and `coachRoutePath` is the single place that
 * turns one into the route serving it.
 */
export const useCoachRouter = () => {
  const route = useRoute();
  const router = useRouter();

  /**
   * Where the student is within the shell, relative to `/coach`: `rewards/cashout`,
   * `badges/<slug>`, or null on the bare chat. The surfaces that route their own sub-views read
   * this and switch on its segments.
   */
  const currentPath = computed<string | null>(() => {
    const path = route.path.replace(/\/+$/, '');
    if (!path.startsWith(`${COACH_ROUTE_INDEX}/`)) {
      return null;
    }
    return path.slice(COACH_ROUTE_INDEX.length + 1) || null;
  });

  /**
   * The single writer. The rest of the query rides along — the Library's `?keyword` is the
   * student's search and belongs to them, not to the page they are leaving. `?navigation=`, the
   * legacy query key old links still address these surfaces by, never reaches a `/coach` URL at all:
   * `coachShellRedirect` spends it at `/dashboard`, which is the only door it arrives through.
   */
  const pushCoachRoute = (path: string, { dropKeyword = false }: { dropKeyword?: boolean } = {}) => {
    const query = { ...route.query };
    if (dropKeyword) {
      delete query.keyword;
    }
    return router.push({ path, query });
  };

  /** Opens a catalogue path — a page and its own segments — as the route that serves it. */
  const openPath = (path: string) => pushCoachRoute(coachRoutePath(path));

  // Navigate to a top-level coach page (optionally one of its nested sub-views) by name: `page`
  // alone, or `page` plus `subpath` (e.g. navigateToPage({ page: 'rewards', subpath:
  // 'westpac-cashout/faq' })). Always an absolute destination, so cross-page links (Rewards ->
  // Account) never mint a path no page owns.
  const navigateToPage = ({ page, subpath }: { page: CoachPage; subpath?: string }) =>
    openPath(subpath ? `${page}/${subpath}` : page);

  // Go back one level within the current coach page: by default pops one path segment
  // (rewards/cashout/faq -> rewards/cashout), keeping the Library/Explore search keyword. `toRoot`
  // (or going back from the last segment) returns to the bare shell and also drops the keyword so
  // it doesn't linger. Throttled (leading only): `goBack` reads the route, which only updates once
  // the router commits, so a rapid second click (e.g. back-button double click) would read a stale
  // snapshot and pop the wrong level — repeat calls within 500ms of the last pop are dropped.
  // Throttle, not debounce: repeated clicking must not extend the dead window indefinitely.
  const goBack = throttle(({ toRoot = false }: { toRoot?: boolean } = {}) => {
    const path = currentPath.value;
    if (!path) {
      return;
    }
    const lastSlash = path.lastIndexOf('/');
    if (!toRoot && lastSlash > 0) {
      void pushCoachRoute(`${COACH_ROUTE_INDEX}/${path.substring(0, lastSlash)}`);
      return;
    }
    void pushCoachRoute(COACH_ROUTE_INDEX, { dropKeyword: true });
  }, 500, { trailing: false });

  return { currentPath, openPath, navigateToPage, goBack };
};
